import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cleanupInactivePages } from 'hooks/usePageInMemory';
import * as cognitoService from 'services/cognito';
import * as idb from 'utils/shared/idbUrl';
import useRecaptcha from 'hooks/useRecaptcha';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import Accessibility from 'components/shared/Accessibility';
import ConfigModal from 'components/shared/ConfigModal';
import Dropdown from 'components/shared/Dropdown';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import ThemeSwitcher from 'components/shared/ThemeSwitcher';
import * as authorizeService from 'services/authorize';
import { dateFormat } from 'utils/shared/utils';
import { MainContext } from '../contexts/MainContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faSearch, faSignIn, faSignOut, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import i18n from '../utils/i18n';

import { availableLanguages, cdnUrl } from '../constants/generic';
import ReactCountryFlag from 'react-country-flag';
import { useClickAway } from 'hooks/useClickAway';
import { logoutAs } from '../services/config';
import { showSuccess } from 'utils/utils';
import DifferentUserModal from './DifferentUserModal';
import Button from 'components/ui/Button/Button';
import { clearIdb } from '../utils/site';

interface HeaderProps {
  logoUrl: string;
}

const Header: FC<HeaderProps> = ({ logoUrl }) => {
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const { proposal, setUser, user, setLoginAsUser, loginAsUser, globals, initSiteData } =
    useContext(MainContext);
  const [hamburgerState, setHamburgerState] = useState(false);
  const navigate = useNavigate();
  const [isHoverElement, setIsHoverElement] = useState(-2);
  const { pathname } = useLocation();
  const [lang, setLang] = useState(localStorage.getItem('language') || 'en');
  const [proposalLength, setProposalLength] = useState(0);
  const userRef = useClickAway(() => setIsHoverElement(-2));
  const [packagesAvailable, setPackagesAvailable] = useState(false);
  const [flightsAvailable, setFlightsAvailable] = useState(false);
  const [openDifferentUserModal, setOpenDifferentUserModal] = useState(false);
  const { handleRecapcha } = useRecaptcha();

  const [latestPageUrl, setLatestPageUrl] = useState<any>();
  const location = useLocation();

  useEffect(() => {
    cleanupInactivePages();
    const latestPageUrl = localStorage.getItem('latestPageUrl');
    if (latestPageUrl) {
      setLatestPageUrl(latestPageUrl);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (initSiteData?.other?.packagesAvailable) {
      setPackagesAvailable(true);
    }
    if (initSiteData?.other?.flightsAvailbale) {
      setFlightsAvailable(true);
    }
  }, [initSiteData]);

  useEffect(() => {
    (async () => {
      if (proposal) {
        const length =
          proposal.dynamic.length +
          proposal.hotels.length +
          proposal.ilPackages.length +
          proposal.transfers.length +
          (proposal.existingProposal?.listOfProducts.filter(Boolean).length || 0) +
          proposal.flights.length;
        setProposalLength(length);

        const prevProposal = await idb.getProposal('proposal');
        if (JSON.stringify(prevProposal) !== JSON.stringify(proposal)) {
          idb.saveProposal('proposal', proposal);
        }
      }
    })();
  }, [proposal]);

  const getToRoute = (url: any) => {
    if (user?.config?.base?.rememberModeEnabled) {
      const newlatestPageUrl = localStorage.getItem('latestPageUrl') || latestPageUrl;
      if (newlatestPageUrl) {
        try {
          const map = JSON.parse(newlatestPageUrl as string);
          if (map && typeof map === 'object') {
            const lowerItemUrl = url.toLowerCase();

            const matchedKey = Object.keys(map).find((k) => {
              const lowerKey = k.toLowerCase();
              if (lowerItemUrl.includes('hotel') && lowerItemUrl.includes('2')) {
                return lowerKey.includes('2') && lowerKey.includes('hotel');
              }

              if (lowerItemUrl.includes('dynamic')) {
                return lowerKey.includes('dynamic');
              }

              if (!lowerItemUrl.includes(lowerKey)) return false;

              return true;
            });
            if (
              matchedKey &&
              map[matchedKey] &&
              Array.isArray(map[matchedKey]) &&
              map[matchedKey].length > 0
            ) {
              return map[matchedKey][map[matchedKey].length - 1];
            }
          }
        } catch (_) { }
      }
    }

    return url;
  };

  useEffect(() => {
    if (lang === 'he') {
      document.body.classList.add('rtl');
    }
  }, []); // eslint-disable-line

  const onPressHanburger = () => {
    document.querySelector('.header-fields')!.classList.toggle('open');
    setHamburgerState(!hamburgerState);
  };

  const onClickProposal = () => {
    if (!proposalLength) {
      sessionStorage.setItem('proposalActiveTab', '1');
    }
    navigate('/proposal');
    onClickHeaderCell();
  };

  const onClickLogoff = async () => {
    clearIdb();

    if (globals?.useBeproCognito) {
      await cognitoService.redirectToBeproCognitoLogin();
      setUser(undefined);
    } else if (globals?.useCognito) {
      setUser(await cognitoService.logOut());
    } else {
      await authorizeService.logout();
      setUser(undefined);
      navigate('/login');
    }
  };

  const onClickHeaderCell = () => {
    document.querySelector('.header-fields')!.classList.remove('open');
    setHamburgerState(false);
  };

  const onChangeLang = (lang: string) => {
    setLang(lang);
    localStorage.setItem('language', lang);

    i18n.changeLanguage(lang);
    if (lang === 'he') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  };
  const selectedCountry = availableLanguages.find((l) => l.lang === lang)?.country || '';

  const onClickSearchMobile = () => {
    const element = document.querySelector('.search-wrapper') as HTMLElement;

    element?.classList.add('visible-responsive');
  };

  const resetLoginAsUser = async () => {
    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;
    setUser(loginAsUser);
    setLoginAsUser(undefined);

    localStorage.setItem('user', JSON.stringify(loginAsUser));
    localStorage.removeItem('loginAsUser');

    logoutAs(recaptchaToken);

    showSuccess(t('mo.logged_as_user_reset_successfully'));
  };

  const hideHeader = location.pathname.includes('proposal/trip-builder');

  if (hideHeader) {
    return null;
  }

  return (
    <Fragment>
      <div className={"header" + (loginAsUser ? ' header-login-as' : '')}>
        <Link className="header-logo-wr" to="/" onClick={onClickHeaderCell}>
          <img
            className="header-logo"
            src={logoUrl.startsWith('http') || logoUrl.includes('/static/media/') ? logoUrl : cdnUrl + logoUrl}
            alt="Logo"
          />
        </Link>
        {!!urlParams.get('checkIn') && (
          <div className="header-search" onClick={onClickSearchMobile}>
            <FontAwesomeIcon icon={faSearch as any} className="search-img" />
            <div className="header-search-text">
              <div className="header-search-city">{urlParams.get('city')}</div>
              <div className="header-search-date">
                <div>{dateFormat(urlParams.get('checkIn')!, 'dd MMM')}</div>
                {!!urlParams.get('checkOut') && (
                  <Fragment>
                    <FontAwesomeIcon icon={faCircle as any} />
                    <div>{dateFormat(urlParams.get('checkOut')!, 'dd MMM')}</div>
                  </Fragment>
                )}
              </div>
            </div>
          </div>
        )}

        <span className="hamburger">
          <input
            id="hamburger__toggle"
            type="checkbox"
            onChange={onPressHanburger}
            checked={hamburgerState}
          />
          <label className="hamburger__btn" htmlFor="hamburger__toggle">
            <span></span>
          </label>
        </span>
        <div className="header-fields">
          {user?.isAuth && (
            <Fragment>
              <Link className="header-cell" to={getToRoute('/hotels')} onClick={onClickHeaderCell}>
                <span
                  className={
                    'header-cell-text' +
                    ((pathname.includes('hotel') || pathname.includes('hotels')) &&
                      !pathname.includes('2')
                      ? ' active'
                      : '')
                  }
                >
                  {t('hotel.s') + ' 1'}
                </span>
              </Link>
              <Link className="header-cell" to={getToRoute('/hotels2')} onClick={onClickHeaderCell}>
                <span
                  className={
                    'header-cell-text' +
                    (pathname.includes('hotel2') || pathname.includes('hotels2') ? ' active' : '')
                  }
                >
                  {t('hotel.s') + ' 2'}
                </span>
              </Link>
              {packagesAvailable && (
                <Link
                  className={'header-cell'}
                  to={getToRoute('/ilpackages')}
                  onClick={onClickHeaderCell}
                >
                  <span
                    className={
                      'header-cell-text' +
                      (pathname.toLowerCase().includes('ilpackage') ? ' active' : '')
                    }
                  >
                    ILPackages
                  </span>
                </Link>
              )}
              {flightsAvailable && (
                <Link
                  className={'header-cell'}
                  to={getToRoute('/flights')}
                  onClick={onClickHeaderCell}
                >
                  <span
                    className={
                      'header-cell-text' +
                      (pathname.toLowerCase().includes('flight') ? ' active' : '')
                    }
                  >
                    {t('flight.s')}
                  </span>
                </Link>
              )}
              {flightsAvailable && (
                <Link
                  className={'header-cell'}
                  to={getToRoute('/dynamic-fh')}
                  onClick={onClickHeaderCell}
                >
                  <span
                    className={
                      'header-cell-text' +
                      (pathname.toLowerCase().includes('dynamic') ? ' active' : '')
                    }
                  >
                    {t('dyn.fl_htl')}
                  </span>
                </Link>
              )}
              <Link
                className="header-cell"
                to={getToRoute('/transfers')}
                onClick={onClickHeaderCell}
              >
                <span
                  className={'header-cell-text' + (pathname.includes('transfer') ? ' active' : '')}
                >
                  {t('transfer.s')}
                </span>
              </Link>
              <div className="header-cell" onClick={onClickHeaderCell}>
                <Link
                  to={'group'}
                  onClick={onClickHeaderCell}
                  className={'header-cell-text' + (pathname === '/group' ? ' active' : '')}
                >
                  {t('group.group')}
                </Link>
              </div>
              <div className="header-cell" onClick={onClickProposal}>
                <span className={"header-cell-text" +
                  (pathname.includes('proposal') ? ' active' : '')} id="proposal-header" onClick={onClickHeaderCell}>
                  {t('proposal.proposal')}
                  {!!proposalLength && <span className="pr-badge">{proposalLength}</span>}
                </span>
              </div>
              <div className="header-cell" onClick={onClickHeaderCell}>
                <Link
                  to={'my-orders'}
                  onClick={onClickHeaderCell}
                  className={'header-cell-text' + (pathname === '/my-orders' ? ' active' : '')}
                >
                  {t('mo.mo')}
                </Link>
              </div>

              <div className="header-oposite"></div>

              <div className="header-cell mr-5" ref={userRef}>
                <FontAwesomeIcon
                  icon={faUser as any}
                  className="s-history-icon"
                  onClick={() => setIsHoverElement(isHoverElement === -1 ? -2 : -1)}
                />

                <div className={'header-dropdown' + (isHoverElement === -1 ? ' open' : '')}>
                  <div className="header-dropdown-row ph-10">
                    <div className="ph-10 mt-10 mb-15 pb-5 bb-1">
                      <span className="text-bold">{t('mo.hello')}, </span>
                      <span className=" text-lg text-contrast">{loginAsUser?.username || user?.username}</span>
                    </div>
                    <div className="ph-10 mt-10 mb-15 text-sm">
                      <span className="text-bold">{t('mo.user_code')}</span>: {loginAsUser?.userCode || user?.userCode} -{' '}
                      {loginAsUser?.username || user?.username}
                    </div>
                    <div className="ph-10 mt-10 mb-15 text-sm">
                      <span className="text-bold">{t('mo.branch')}</span>: {loginAsUser?.branchId || user?.branchId} -{' '}
                      {loginAsUser?.branch?.branchTitle || user?.branch?.branchTitle}
                    </div>
                    <div className="ph-10 mt-10 mb-15 text-sm">
                      <span className="text-bold">{t('mo.department')}</span>: {loginAsUser?.departmentId || user?.departmentId} -{' '}
                      {loginAsUser?.department?.departmentTitle || user?.department?.departmentTitle}
                    </div>

                    <div className='ph-10 mt-10 mb-15 text-sm'>
                      <Button className='btn btn-default btn-sm' title={t('mo.profile')} onClick={() => navigate('/profile')} />
                    </div>

                    {((loginAsUser || user) as any)?.user_group_id >= 40 && <div className='ph-10 mt-10 mb-15 text-sm'>
                      <Button className='btn btn-danger btn-sm' title={t('mo.set_different_user')} onClick={() => setOpenDifferentUserModal(true)} />
                    </div>}

                    {loginAsUser && (
                      <div className='ph-10 mt-10 mb-15 text-sm'>
                        <div className='login-as'>
                          <div className='login-as-head'>
                            <FontAwesomeIcon icon={faUser as any} /> {t('mo.login_as')}
                          </div>
                          <div className='login-as-body'>
                            <div>
                              <div>{t('mo.department')}: <span className='text-bold'>{user?.department?.departmentTitle}</span></div>
                              <div>{t('mo.branch')}: <span className='text-bold'>{user?.branch?.branchTitle}</span></div>
                              <div>{t('mo.user_code')}: <span className='text-bold'>{user?.userCode}</span></div>
                              <div>{t('mo.username')}: <span className='text-bold'>{user?.username}</span></div>

                              <Button className='btn btn-default btn-sm mt-10 ' title={t('mo.reset')} onClick={resetLoginAsUser} />
                            </div>

                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="header-cell mr-5">
                <ConfigModal />
              </div>

              <div className="header-cell mr-5">
                <ScreenshotSupport />
              </div>

              <div className="header-cell mr-5">
                <ThemeSwitcher />
              </div>

              <div className="header-cell mr-5">
                <Dropdown
                  opener={
                    selectedCountry === 'us' ? (
                      <img src={require('../assets/img/us.png')} alt="" />
                    ) : (
                      <ReactCountryFlag svg countryCode={selectedCountry} />
                    )
                  }
                >
                  {availableLanguages.map((lang) => (
                    <div
                      className="c-dropdown-row"
                      key={lang.country}
                      onClick={() => onChangeLang(lang.lang)}
                    >
                      {lang.country === 'us' ? (
                        <img src={require('../assets/img/us.png')} alt="" />
                      ) : (
                        <ReactCountryFlag svg countryCode={lang.country} />
                      )}
                      <span className="ml-5 text-sm">{lang.name}</span>
                    </div>
                  ))}
                </Dropdown>
              </div>
              <div className="header-cell" onClick={onClickLogoff}>
                <span className="header-cell-text">
                  <FontAwesomeIcon icon={faSignOut as any} />
                </span>
              </div>
            </Fragment>
          )}

          {!user?.isAuth && (
            <Fragment>
              <div className="header-oposite"></div>
              <div className="header-cell" onClick={onClickHeaderCell}>
                {globals?.useCognito ? (
                  <a href="/" onClick={() => cognitoService.redirectToLogin()}>
                    <FontAwesomeIcon icon={faSignIn as any} />
                  </a>
                ) : (
                  <Link
                    to={'login'}
                    className={'header-cell-text' + (pathname === '/login' ? ' active' : '')}
                  >
                    <FontAwesomeIcon icon={faSignIn as any} />
                  </Link>
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>
      <Accessibility />
      {openDifferentUserModal && (
        <DifferentUserModal openModal={openDifferentUserModal} setOpenModal={setOpenDifferentUserModal} />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(Header, 'Header');
